/**
 * Pestaña Rotaciones: sideout% / break% / neto por posición del armador
 * (P1..P6), la primera tabla que pide cualquier entrenador.
 *
 * Cuando la rotación no se puede reconstruir (falta el `lineup_set` o no se
 * sabe quién es el armador) se explica en castellano en vez de mostrar seis
 * filas de ceros, que es lo que haría creer que el equipo no hizo nada.
 */
import { BarChart } from '../../components/Charts';
import { Court, heatTone } from '../../components/Court';
import { Card } from '../../components/States';
import type { MessageKey } from '../../i18n/index';
import { useT } from '../../i18n/use-t';
import { pct, signed } from '../../lib/format';
import type { RotationLine, RotationReport, TeamRotationReport } from '../../lib/projections';

const UNTRACKED_KEYS: Record<string, MessageKey> = {
  'no-lineup': 'rotations.noLineup',
  'no-setter': 'rotations.noSetter',
};

function TeamRotations({
  team,
  side,
}: {
  team: TeamRotationReport;
  side: 'home' | 'away';
}): JSX.Element {
  const t = useT();

  if (!team.tracked) {
    const key = UNTRACKED_KEYS[team.untrackedReason ?? 'no-lineup'];
    return (
      <Card title={<span className={`side-name ${side} team-title`}>{team.name}</span>}>
        <p className="empty">
          {key ? t(key) : t('rotations.untracked')}
          {team.unassignedRallies > 0
            ? t('rotations.unassignedSuffix', { count: team.unassignedRallies })
            : ''}
        </p>
      </Card>
    );
  }

  const bars = team.rotations.map((line) => ({
    label: line.label,
    value: line.sideoutPct,
    title: t('rotations.barTitle', {
      label: line.label,
      pct: pct(line.sideoutPct),
      won: line.sideoutPoints,
      total: line.ralliesReceived,
    }),
    display: pct(line.sideoutPct),
  }));

  return (
    <Card
      title={<span className={`side-name ${side} team-title`}>{team.name}</span>}
      actions={
        team.unassignedRallies > 0 ? (
          <span className="chip warn" title={t('rotations.unassignedTitle')}>
            {t('rotations.unassignedChip', { count: team.unassignedRallies })}
          </span>
        ) : null
      }
    >
      <div className="court-row" style={{ marginBottom: 14 }}>
        <RotationCourt team={team} side={side} metric="sideout" />
        <RotationCourt team={team} side={side} metric="break" />
      </div>
      <p className="court-note" style={{ marginTop: 0 }}>
        {t('matchViews.rotCourtHint')}
      </p>

      <h3>{t('rotations.sideoutByRotation')}</h3>
      <BarChart data={bars} side={side} max={1} format={pct} />

      <div className="table-wrap" style={{ marginTop: 12 }}>
        <table>
          <thead>
            <tr>
              <th>{t('rotations.colRotation')}</th>
              <th className="num">{t('rotations.colRallies')}</th>
              <th className="num" title={t('rotations.colReceivedTitle')}>
                {t('rotations.colReceived')}
              </th>
              <th className="num">{t('rotations.colSideout')}</th>
              <th className="num" title={t('rotations.colServedTitle')}>
                {t('rotations.colServed')}
              </th>
              <th className="num">{t('rotations.colBreak')}</th>
              <th className="num" title={t('rotations.colNetTitle')}>
                {t('rotations.colNet')}
              </th>
            </tr>
          </thead>
          <tbody>
            {team.rotations.map((line) => (
              <tr key={line.rotation}>
                <td>{line.label}</td>
                <td className="num">{line.rallies}</td>
                <td className="num">{line.ralliesReceived}</td>
                <td className="num">{line.ralliesReceived > 0 ? pct(line.sideoutPct) : '·'}</td>
                <td className="num">{line.ralliesServed}</td>
                <td className="num">{line.ralliesServed > 0 ? pct(line.breakPct) : '·'}</td>
                <td className={`num ${line.netPoints < 0 ? 'neg' : line.netPoints > 0 ? 'pos' : ''}`}>
                  {signed(line.netPoints)}
                </td>
              </tr>
            ))}
            <tr className="total-row">
              <td>{t('common.total')}</td>
              <td className="num">{sum(team, 'rallies')}</td>
              <td className="num">{sum(team, 'ralliesReceived')}</td>
              <td className="num">
                {effPctSafe(sum(team, 'sideoutPoints'), sum(team, 'ralliesReceived'))}
              </td>
              <td className="num">{sum(team, 'ralliesServed')}</td>
              <td className="num">
                {effPctSafe(sum(team, 'breakPoints'), sum(team, 'ralliesServed'))}
              </td>
              <td className="num">{signed(sum(team, 'pointsWon') - sum(team, 'pointsLost'))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function sum(team: TeamRotationReport, key: keyof TeamRotationReport['rotations'][number]): number {
  return team.rotations.reduce((total, line) => {
    const value = line[key];
    return total + (typeof value === 'number' ? value : 0);
  }, 0);
}

function effPctSafe(numerator: number, denominator: number): string {
  if (denominator === 0) return '·';
  return pct(numerator / denominator);
}

export function RotationsTab({ report }: { report: RotationReport }): JSX.Element {
  const t = useT();
  return (
    <div>
      <p className="hint" style={{ marginTop: 0 }}>
        {t('rotations.intro')}
      </p>
      <TeamRotations team={report.home} side="home" />
      <TeamRotations team={report.away} side="away" />
    </div>
  );
}

/**
 * La rotación sobre la cancha, como la lee un entrenador: cada zona es la
 * rotación con el armador EN esa zona (R1 = armador en 1, sacando). Pintada
 * con la rampa del heatmap sobre el rango de las seis, así la rotación floja
 * salta a la vista sin leer números.
 */
function RotationCourt({
  team,
  side,
  metric,
}: {
  team: TeamRotationReport;
  side: 'home' | 'away';
  metric: 'sideout' | 'break';
}): JSX.Element {
  const t = useT();
  const valueOf = (line: RotationLine): number | null =>
    metric === 'sideout'
      ? line.ralliesReceived > 0
        ? line.sideoutPct
        : null
      : line.ralliesServed > 0
        ? line.breakPct
        : null;
  const values = team.rotations.map(valueOf).filter((v): v is number => v !== null);
  const min = values.length > 0 ? Math.min(...values) : 0;
  const max = values.length > 0 ? Math.max(...values) : 0;
  const zones = team.rotations
    .filter((line) => line.rotation >= 1 && line.rotation <= 6)
    .map((line) => {
      const value = valueOf(line);
      const won = metric === 'sideout' ? line.sideoutPoints : line.breakPoints;
      const total = metric === 'sideout' ? line.ralliesReceived : line.ralliesServed;
      return {
        zone: line.rotation,
        value: value === null ? '·' : pct(value),
        sub: t('matchViews.rotSub', { won, total }),
        ...(value === null ? {} : heatTone(value, min, max)),
        title: `${line.label} · ${value === null ? '·' : pct(value)} (${won}/${total})`,
      };
    });
  const what = metric === 'sideout' ? t('matchViews.rotCourtSideout') : t('matchViews.rotCourtBreak');
  return (
    <figure className="court-fig">
      <figcaption>{what}</figcaption>
      <Court
        label={t('matchViews.rotCourtLabel', { team: team.name, what })}
        sides={[{ team: side, zones, zonePrefix: 'R' }]}
        width={230}
      />
    </figure>
  );
}
